import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { getAmbientEngine, type AmbientMode, type AmbientWorld } from "@/lib/ambient";

const STORAGE_KEY = "jozori.ambient.v1";

type Persisted = { mode: AmbientMode; volume: number; muted: boolean };

type AmbientCtx = {
  mode: AmbientMode;
  world: AmbientWorld;
  volume: number;
  muted: boolean;
  setMode: (m: AmbientMode) => void;
  setWorld: (w: AmbientWorld) => void;
  setVolume: (v: number) => void;
  setMuted: (m: boolean) => void;
};

const Ctx = createContext<AmbientCtx | null>(null);

function loadPersisted(): Persisted {
  if (typeof window === "undefined") return { mode: "silent", volume: 0.25, muted: false };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { mode: "silent", volume: 0.25, muted: false, ...JSON.parse(raw) };
  } catch { /* noop */ }
  return { mode: "silent", volume: 0.25, muted: false };
}

export function AmbientProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<AmbientMode>("silent");
  const [world, setWorldState] = useState<AmbientWorld>(null);
  const [volume, setVolumeState] = useState(0.25);
  const [muted, setMutedState] = useState(false);

  // hydrate from storage on mount (client-only)
  useEffect(() => {
    const p = loadPersisted();
    setModeState(p.mode);
    setVolumeState(p.volume);
    setMutedState(p.muted);
    const eng = getAmbientEngine();
    eng.setVolume(p.volume);
    eng.setMuted(p.muted);
    // do not auto-start audio without a gesture
    const onFirstGesture = () => {
      eng.unlock();
      if (p.mode !== "silent") eng.setMode(p.mode);
      window.removeEventListener("pointerdown", onFirstGesture);
      window.removeEventListener("keydown", onFirstGesture);
    };
    window.addEventListener("pointerdown", onFirstGesture, { once: true });
    window.addEventListener("keydown", onFirstGesture, { once: true });
    return () => {
      window.removeEventListener("pointerdown", onFirstGesture);
      window.removeEventListener("keydown", onFirstGesture);
    };
  }, []);

  const persist = (next: Partial<Persisted>) => {
    if (typeof window === "undefined") return;
    try {
      const cur = loadPersisted();
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...cur, ...next }));
    } catch { /* noop */ }
  };

  const setMode = useCallback((m: AmbientMode) => {
    setModeState(m);
    getAmbientEngine().setMode(m);
    persist({ mode: m });
  }, []);

  const setWorld = useCallback((w: AmbientWorld) => {
    setWorldState(w);
    getAmbientEngine().setWorld(w);
  }, []);

  const setVolume = useCallback((v: number) => {
    setVolumeState(v);
    getAmbientEngine().setVolume(v);
    persist({ volume: v });
  }, []);

  const setMuted = useCallback((m: boolean) => {
    setMutedState(m);
    getAmbientEngine().setMuted(m);
    persist({ muted: m });
  }, []);

  return (
    <Ctx.Provider value={{ mode, world, volume, muted, setMode, setWorld, setVolume, setMuted }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAmbient(): AmbientCtx {
  const c = useContext(Ctx);
  if (!c) {
    // fail-soft: callers outside provider become no-ops
    return {
      mode: "silent", world: null, volume: 0, muted: true,
      setMode: () => {}, setWorld: () => {}, setVolume: () => {}, setMuted: () => {},
    };
  }
  return c;
}
